import { useContext, useMemo, useState } from "react";
import lod_ from "lodash";
import { DateTime } from "luxon";
import { alpha, ButtonBase, Icon, IconButton, Popover, Tooltip } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { useMaterialUIController } from "context";
import i18n from "i18n";
import ChartFiltersContext from "./context";
import computeChartFiltersContext from "./computeChartFiltersContext";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T|$)/;

// Same format as the date picker (DateFilter "d M Y H:i"), e.g. "14 sept. 2026 12:00"
const DATE_FORMAT = "d MMM yyyy HH:mm";

/**
 * ISO string as a luxon date (local time), null when the value is not a date
 */
function toDate(value, language) {
	if (lod_.isString(value) && ISO_DATE.test(value)) {
		const date = DateTime.fromISO(value);
		if (date.isValid) return date.setLocale(language);
	}
	return null;
}

/**
 * Text of a value, dates (ISO strings) are formatted with the user language
 */
function formatValue(value, language) {
	const date = toDate(value, language);
	return date ? date.toFormat(DATE_FORMAT) : String(value);
}

const WARNING_COLOR = "#fb8c00";
const INFO_COLOR = "#1A73E8";
// Horizontal padding of the popover blocks (px)
const PADDING_X = 24;

/**
 * Neutral (grey) tone, readable in light and dark mode
 */
const neutral = (darkMode, opacity) => alpha(darkMode ? "#ffffff" : "#344767", opacity);

/**
 * Orange text color, readable in light and dark mode
 */
const warningText = darkMode => (darkMode ? "#ffb74d" : "#b45f06");

/**
 * Text explaining an overridden filter (always by the page), null when the row is not overridden
 */
function getOverrideText(row, t) {
	return row.overridden ? t("CHARTS.FILTERS_CONTEXT.overriddenByPage") : null;
}

/**
 * Soft tinted chip with a value (greyed and struck through when the filter is overridden)
 */
const ValueChip = ({ children, darkMode, overridden }) => (
	<MDBox
		component="span"
		title={children}
		sx={{
			display: "inline-flex",
			alignItems: "center",
			height: 24,
			maxWidth: "100%",
			px: "8px",
			borderRadius: "6px",
			fontSize: "0.8125rem",
			fontWeight: 600,
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
			color: overridden ? "text.disabled" : "text.primary",
			bgcolor: overridden ? neutral(darkMode, 0.12) : alpha(INFO_COLOR, darkMode ? 0.18 : 0.08),
			...(overridden && { textDecoration: "line-through" })
		}}
	>
		{children}
	</MDBox>
);

/**
 * Method + value chips of a row. A range is on two lines: "du [from]" / "au [to]"
 */
const FilterValues = ({ row, language, darkMode }) => {
	const t = i18n.t.bind(i18n);
	const textColor = row.overridden ? "text.disabled" : "text.secondary";
	const lineSx = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px" };

	if (row.method === "between") {
		const bounds = [
			["rangeFrom", row.range.$gte ?? row.range.$gt],
			["rangeTo", row.range.$lte ?? row.range.$lt]
		].filter(([, value]) => !lod_.isNil(value));
		return (
			<MDBox sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
				{bounds.map(([key, value]) => (
					<MDBox key={key} sx={lineSx}>
						<MDBox
							component="span"
							sx={{ minWidth: "1.5rem", fontSize: "0.8125rem", color: textColor }}
						>
							{t(`CHARTS.FILTERS_CONTEXT.${key}`)}
						</MDBox>
						<ValueChip darkMode={darkMode} overridden={row.overridden}>
							{formatValue(value, language)}
						</ValueChip>
					</MDBox>
				))}
			</MDBox>
		);
	}

	return (
		<MDBox sx={lineSx}>
			<MDBox component="span" sx={{ mr: "2px", fontSize: "0.8125rem", color: textColor }}>
				{t(`CHARTS.FILTERS_CONTEXT.METHODS.${row.method.replace("$", "")}`)}
			</MDBox>
			{row.values.map((value, index) => (
				<ValueChip key={index} darkMode={darkMode} overridden={row.overridden}>
					{formatValue(value, language)}
				</ValueChip>
			))}
		</MDBox>
	);
};

/**
 * One filter: label, then the method and value chips.
 * An overridden filter is greyed, followed by a line saying who overrides it.
 */
const FilterRow = ({ row, language, darkMode }) => {
	const t = i18n.t.bind(i18n);
	const overrideText = getOverrideText(row, t);

	return (
		<MDBox sx={{ "&:not(:first-of-type)": { mt: "14px" } }}>
			<MDBox
				mb="4px"
				sx={{
					fontSize: "0.8125rem",
					fontWeight: 600,
					color: darkMode ? "white.main" : "dark.main",
					lineHeight: 1.4
				}}
			>
				{row.label}
			</MDBox>
			<FilterValues row={row} language={language} darkMode={darkMode} />
			{overrideText && (
				<MDBox display="flex" alignItems="center" gap="8px" mt="6px">
					<MDBox
						component="span"
						sx={{
							flexShrink: 0,
							width: 7,
							height: 7,
							borderRadius: "50%",
							bgcolor: WARNING_COLOR
						}}
					/>
					<MDBox component="span" sx={{ fontSize: "0.75rem", color: warningText(darkMode) }}>
						{overrideText}
					</MDBox>
				</MDBox>
			)}
		</MDBox>
	);
};

/**
 * Section of the popover: small uppercase caption, then the filters
 */
const FiltersSection = ({ title, rows, language, darkMode }) => (
	<MDBox sx={{ px: `${PADDING_X}px`, py: "16px" }}>
		<MDBox
			mb="14px"
			sx={{
				fontSize: "0.6875rem",
				fontWeight: 600,
				letterSpacing: "0.08em",
				textTransform: "uppercase",
				color: "text.secondary"
			}}
		>
			{title}
		</MDBox>
		{rows.map((row, index) => (
			<FilterRow key={`${row.key}-${index}`} row={row} language={language} darkMode={darkMode} />
		))}
	</MDBox>
);

/**
 * Thin line between blocks, edge to edge of the popover
 */
const SectionDivider = ({ darkMode }) => (
	<MDBox sx={{ height: "1px", bgcolor: neutral(darkMode, darkMode ? 0.16 : 0.1) }} />
);

/**
 * Button displayed in the header of a widget, next to the options menu: number of filters applied to the
 * widget (its own filters + the page ones) and, in a popover, the detail of the data source and filters
 * Displayed only in a charts page (needs the ChartFiltersContext)
 */
const ChartFiltersContextButton = ({ chart }) => {
	const t = i18n.t.bind(i18n);
	const pageContext = useContext(ChartFiltersContext);
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;
	const [anchorEl, setAnchorEl] = useState(null);

	const language = pageContext?.language ?? "fr";
	const context = useMemo(() => {
		if (!pageContext || lod_.isNil(chart?.request)) {
			return null;
		}
		return computeChartFiltersContext(chart, pageContext.filters, pageContext.definitions, {
			language,
			pageDisplayDates: pageContext.displayDates,
			defaultDateLabel: i18n.t("CHARTS.FILTERS_CONTEXT.period")
		});
	}, [chart, pageContext, language]);

	if (!context || (context.sources.length === 0 && context.count === 0)) {
		return null;
	}

	const { sources, widgetRows, pageRows, count, hasOverride } = context;
	const open = Boolean(anchorEl);
	const borderColor = darkMode ? "rgba(255,255,255,0.3)" : "rgba(52,71,103,0.3)";

	return (
		<>
			<Tooltip placement="top" title={t("CHARTS.FILTERS_CONTEXT.tooltip")}>
				<ButtonBase
					onClick={e => setAnchorEl(e.currentTarget)}
					aria-haspopup="dialog"
					aria-expanded={open}
					sx={{
						position: "relative",
						display: "inline-flex",
						alignItems: "center",
						gap: 0.5,
						height: "1.9rem",
						px: 1.25,
						mr: 0.5,
						borderRadius: "1rem",
						border: `1px solid ${open ? INFO_COLOR : borderColor}`,
						color: "text.primary",
						transition: "background-color 150ms, border-color 150ms, box-shadow 150ms",
						...(open && {
							backgroundColor: alpha(INFO_COLOR, 0.1),
							boxShadow: `0 0 0 3px ${alpha(INFO_COLOR, 0.2)}`
						}),
						"&:hover, &.Mui-focusVisible": {
							borderColor: INFO_COLOR,
							backgroundColor: alpha(INFO_COLOR, 0.1)
						}
					}}
				>
					<Icon fontSize="small">filter_alt</Icon>
					<MDTypography variant="button" fontWeight="bold" lineHeight={1}>
						{count}
					</MDTypography>
					{hasOverride && (
						<MDBox
							component="span"
							sx={{
								position: "absolute",
								top: -3,
								right: -3,
								width: 9,
								height: 9,
								borderRadius: "50%",
								bgcolor: WARNING_COLOR,
								border: `1.5px solid ${darkMode ? "#1a2035" : "#fff"}`
							}}
						/>
					)}
				</ButtonBase>
			</Tooltip>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{
					paper: {
						sx: ({ palette }) => ({
							width: 320,
							maxWidth: "92vw",
							mt: 1,
							borderRadius: "16px",
							overflow: "visible",
							// Theme popovers are transparent: use the card background (dark theme) or white
							backgroundColor: palette.background.card ?? palette.white.main,
							border: `1px solid ${darkMode ? "rgba(255,255,255,0.12)" : "rgba(52,71,103,0.12)"}`,
							// Theme shadow is too light for a floating panel
							boxShadow: darkMode
								? "0 12px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)"
								: "0 12px 32px rgba(52,71,103,0.18), 0 2px 8px rgba(52,71,103,0.1)"
						})
					}
				}}
			>
				{/* Header: title + source */}
				<MDBox sx={{ px: `${PADDING_X}px`, pt: "20px", pb: "16px" }}>
					<MDBox display="flex" alignItems="center" justifyContent="space-between">
						<MDTypography variant="h6" fontWeight="bold" fontSize="1.0625rem">
							{t("CHARTS.FILTERS_CONTEXT.title")}
						</MDTypography>
						<IconButton
							size="small"
							aria-label={t("CHARTS.FILTERS_CONTEXT.close")}
							onClick={() => setAnchorEl(null)}
							sx={{ mr: "-8px" }}
						>
							<Icon>close</Icon>
						</IconButton>
					</MDBox>
					{sources.map(source => (
						<MDBox
							key={source.collection}
							display="flex"
							alignItems="center"
							gap="8px"
							mt="10px"
							sx={{ fontSize: "0.875rem" }}
						>
							{/* Database icon (not available in the Material Icons font) */}
							<MDBox
								component="svg"
								viewBox="0 0 24 24"
								width="18px"
								height="18px"
								fill="none"
								stroke="currentColor"
								strokeWidth={1.8}
								strokeLinecap="round"
								sx={{ color: "text.secondary", flexShrink: 0 }}
							>
								<ellipse cx="12" cy="5" rx="8" ry="3" />
								<path d="M4 5v7c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
								<path d="M4 12v7c0 1.66 3.58 3 8 3s8-1.34 8-3v-7" />
							</MDBox>
							<MDBox component="span" sx={{ color: "text.secondary" }}>
								{t("CHARTS.FILTERS_CONTEXT.source")}
							</MDBox>
							<MDBox component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
								{source.label}
							</MDBox>
						</MDBox>
					))}
				</MDBox>

				<SectionDivider darkMode={darkMode} />

				{widgetRows.length === 0 && pageRows.length === 0 && (
					<MDBox
						sx={{ px: `${PADDING_X}px`, py: "16px", fontSize: "0.875rem", color: "text.secondary" }}
					>
						{t("CHARTS.FILTERS_CONTEXT.noFilter")}
					</MDBox>
				)}

				{widgetRows.length > 0 && (
					<FiltersSection
						title={t("CHARTS.FILTERS_CONTEXT.onWidget")}
						rows={widgetRows}
						language={language}
						darkMode={darkMode}
					/>
				)}

				{widgetRows.length > 0 && pageRows.length > 0 && <SectionDivider darkMode={darkMode} />}

				{pageRows.length > 0 && (
					<FiltersSection
						title={t("CHARTS.FILTERS_CONTEXT.fromPage")}
						rows={pageRows}
						language={language}
						darkMode={darkMode}
					/>
				)}
			</Popover>
		</>
	);
};

export default ChartFiltersContextButton;
